import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import { BaseTask } from "../../model/task/Task";
import { KanbanCard } from "../../model/kanban/KanbanCard";
import { KanbanColumnStageType } from "../../model/kanban/types/KanbanColumnStageType.enum";
import { Entry } from "../../model/entry/Entry";
import { TaskKind } from "../../model/task/shared/TasksKind.enum";
import { BaseModuleInstance } from "../../model/module/types/module-instance/BaseModuleInstance";
import { CreateEntryDto } from "../../model/entry/dto/CreateNewEntryDto";
import { ChatWidgetCurrent } from "../../model/chat-wiget/types/ChatWigetCurrent.enum";
import { EntryCreatePrefill } from "../../model/entry/types/EntryCreatePrefill";

interface DrawersContextType {
  // Tasks
  task: BaseTask | null;
  setTask: ATVoidFunction<BaseTask | null>;
  //
  taskKind: TaskKind | null;
  setTaskKind: ATVoidFunction<TaskKind | null>;
  //
  tasksReadVisible: boolean;
  setTasksReadVisible: ATVoidFunction<boolean>;
  taskUpdateVisible: boolean;
  setTaskUpdateVisible: ATVoidFunction<boolean>;
  taskCreateVisible: boolean;
  setTaskCreateVisible: ATVoidFunction<boolean>;
  taskReadVisible: boolean;
  setTaskReadVisible: ATVoidFunction<boolean>;
  //
  taskDeleteVisible: boolean;
  setTaskDeleteVisible: ATVoidFunction<boolean>;
  //
  taskDoneVisible: boolean;
  setTaskDoneVisible: ATVoidFunction<boolean>;
  //
  taskAssignVisible: boolean;
  setTaskAssignVisible: ATVoidFunction<boolean>;
  //

  // Kanban
  kanbanCard: KanbanCard | null;
  setKanbanCard: ATVoidFunction<KanbanCard | null>;
  //
  kanbanCardReadVisible: boolean;
  setKanbanCardReadVisible: ATVoidFunction<boolean>;
  kanbanCardUpdateVisible: boolean;
  setKanbanCardUpdateVisible: ATVoidFunction<boolean>;
  kanbanCardCreateVisible: boolean;
  setKanbanCardCreateVisible: ATVoidFunction<boolean>;
  kanbanSettingsVisible: boolean;
  setKanbanSettingsVisible: ATVoidFunction<boolean>;
  kanbanActivityVisible: boolean;
  setKanbanActivityVisible: ATVoidFunction<boolean>;
  //
  kanbanCreateVisible: boolean;
  setKanbanCreateVisible: ATVoidFunction<boolean>;
  //
  kanbanCardMovingReasonVisible: boolean;
  setKanbanCardMovingReasonVisible: ATVoidFunction<boolean>;
  kanbanCardMoveToOtherBoardVisible: boolean;
  setKanbanCardMoveToOtherBoardVisible: ATVoidFunction<boolean>;
  kanbanCardDeleteVisible: boolean;
  setKanbanCardDeleteVisible: ATVoidFunction<boolean>;
  //
  kanbanAutomationVisible: boolean;
  setKanbanAutomationVisible: ATVoidFunction<boolean>;
  //
  onDragKanbanCardWithReasonMessage: ATVoidFunction<string> | null;
  setOnDragKanbanCardWithReasonMessage: ATVoidFunction<ATVoidFunction<string> | null>;
  kanbanCardMovingReasonStageType: KanbanColumnStageType | null;
  setKanbanCardMovingReasonStageType: ATVoidFunction<KanbanColumnStageType | null>;
  //
  selectedKanbanColumnId: string | null;
  setSelectedKanbanColumnId: ATVoidFunction<string | null>;
  selectedEntryForKanbanCard: Entry | null;
  setSelectedEntryForKanbanCard: ATVoidFunction<Entry | null>;
  exportModalVisible: boolean;
  setExportModalVisible: ATVoidFunction<boolean>;

  // Entries
  entry: Entry | null;
  setEntry: ATVoidFunction<Entry | null>;
  //
  entryAssignVisible: boolean;
  setEntryAssignVisible: ATVoidFunction<boolean>;
  entryDetailsVisible: boolean;
  setEntryDetailsVisible: ATVoidFunction<boolean>;
  entryConvertVisible: boolean;
  setEntryConvertVisible: ATVoidFunction<boolean>;
  entryCreateVisible: boolean;
  setEntryCreateVisible: ATVoidFunction<boolean>;
  entryDeleteVisible: boolean;
  setEntryDeleteVisible: ATVoidFunction<boolean>;
  entryDeleteLoading: boolean;
  setEntryDeleteLoading: ATVoidFunction<boolean>;
  entryUpdateVisible: boolean;
  setEntryUpdateVisible: ATVoidFunction<boolean>;
  importExportVisible: boolean;
  setImportExportVisible: ATVoidFunction<boolean>;
  entryAssignBulkVisible: boolean;
  setEntryAssignBulkVisible: ATVoidFunction<boolean>;
  entriesToAssign: string[];
  setEntriesToAssign: ATVoidFunction<string[]>;
  bulkEntryExportVisible: boolean;
  setBulkEntryExportVisible: ATVoidFunction<boolean>;
  //
  isBulkEntryAction: boolean;
  setIsBulkEntryAction: ATVoidFunction<boolean>;
  selectedRows: React.Key[];
  setSelectedRows: Dispatch<SetStateAction<React.Key[]>>;
  duplicateEntries: Entry[];
  setDuplicateEntries: ATVoidFunction<Entry[]>;
  duplicateEntryVisible: boolean;
  setDuplicateEntryVisible: ATVoidFunction<boolean>;
  duplicateEntryInitiateResolveVisible: boolean;
  setDuplicateEntryInitiateResolveVisible: ATVoidFunction<boolean>;
  duplicateEntriesGroups: Record<string, Entry[]> | null;
  setDuplicateEntriesGroups: ATVoidFunction<Record<string, Entry[]> | null>;
  //
  currentOnIgnoreAction?: (() => Promise<void>) | undefined;
  setCurrentOnIgnoreAction: Dispatch<
    SetStateAction<(() => Promise<void>) | undefined>
  >;
  entryCreateAction: ATVoidFunction<CreateEntryDto> | null;
  setEntryCreateAction: React.Dispatch<
    React.SetStateAction<ATVoidFunction<CreateEntryDto> | null>
  >;
  entryCreatePrefill: EntryCreatePrefill | null;
  setEntryCreatePrefill: ATVoidFunction<EntryCreatePrefill | null>;
  tagsManageVisible: boolean;
  setTagsManageVisible: ATVoidFunction<boolean>;

  //
  moduleDefinitionActivityDrawerVisible: boolean;
  setModuleDefinitionActivityDrawerVisible: ATVoidFunction<boolean>;
  moduleInstance: BaseModuleInstance | null;
  setModuleInstance: ATVoidFunction<BaseModuleInstance | null>;

  moduleInstanceReadVisible: boolean;
  setModuleInstanceReadVisible: ATVoidFunction<boolean>;
  //
  subToUnlock: { visible: boolean; title?: string };
  setSubToUnlock: ATVoidFunction<{ visible: boolean; title?: string }>;
  //
  fahmyMenuOpen: boolean;
  setFahmyMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  fahmyCurrent: ChatWidgetCurrent;
  setFahmyCurrent: ATVoidFunction<ChatWidgetCurrent>;
  fahmyShowTooltip: boolean;
  setFahmyShowTooltip: ATVoidFunction<boolean>;
  fahmyIsHovered: boolean;
  setFahmyIsHovered: ATVoidFunction<boolean>;
}

const DrawersContext = createContext<DrawersContextType | undefined>(undefined);

export const useDrawersContext = () => {
  const context = useContext(DrawersContext);

  if (!context) {
    throw new Error("useDrawersContext must be used within a DrawersProvider");
  }

  return context;
};

interface DrawersContextProviderProps {
  children: ReactNode;
}

export const DrawersContextProvider: React.FC<DrawersContextProviderProps> = ({
  children,
}) => {
  // Tasks
  const [task, setTask] = useState<BaseTask | null>(null);
  const [taskKind, setTaskKind] = useState<TaskKind | null>(null);
  const [tasksReadVisible, setTasksReadVisible] = useState(false);
  const [taskUpdateVisible, setTaskUpdateVisible] = useState(false);
  const [taskCreateVisible, setTaskCreateVisible] = useState(false);
  const [taskDeleteVisible, setTaskDeleteVisible] = useState(false);
  const [taskDoneVisible, setTaskDoneVisible] = useState(false);
  const [taskAssignVisible, setTaskAssignVisible] = useState(false);
  const [taskReadVisible, setTaskReadVisible] = useState(false);
  // Kanban
  const [kanbanCard, setKanbanCard] = useState<KanbanCard | null>(null);
  const [kanbanCardReadVisible, setKanbanCardReadVisible] = useState(false);
  const [kanbanCardUpdateVisible, setKanbanCardUpdateVisible] = useState(false);
  const [kanbanCardCreateVisible, setKanbanCardCreateVisible] = useState(false);
  const [kanbanSettingsVisible, setKanbanSettingsVisible] = useState(false);
  const [kanbanActivityVisible, setKanbanActivityVisible] = useState(false);
  const [kanbanCreateVisible, setKanbanCreateVisible] = useState(false);
  const [kanbanCardMovingReasonVisible, setKanbanCardMovingReasonVisible] =
    useState(false);
  const [
    onDragKanbanCardWithReasonMessage,
    setOnDragKanbanCardWithReasonMessage,
  ] = useState<ATVoidFunction<string> | null>(null);
  const [kanbanCardMovingReasonStageType, setKanbanCardMovingReasonStageType] =
    useState<KanbanColumnStageType | null>(null);
  const [
    kanbanCardMoveToOtherBoardVisible,
    setKanbanCardMoveToOtherBoardVisible,
  ] = useState(false);
  const [kanbanCardDeleteVisible, setKanbanCardDeleteVisible] = useState(false);
  const [kanbanAutomationVisible, setKanbanAutomationVisible] = useState(false);
  const [selectedKanbanColumnId, setSelectedKanbanColumnId] = useState<
    string | null
  >(null);
  const [selectedEntryForKanbanCard, setSelectedEntryForKanbanCard] =
    useState<Entry | null>(null);
  const [exportModalVisible, setExportModalVisible] = useState(false);

  // Entries
  const [entry, setEntry] = useState<Entry | null>(null);
  //
  const [entryAssignVisible, setEntryAssignVisible] = useState(false);
  const [entryDetailsVisible, setEntryDetailsVisible] = useState(false);
  const [entryConvertVisible, setEntryConvertVisible] = useState(false);
  const [entryCreateVisible, setEntryCreateVisible] = useState(false);
  const [entryDeleteVisible, setEntryDeleteVisible] = useState(false);
  const [entryDeleteLoading, setEntryDeleteLoading] = useState(false);
  const [entryUpdateVisible, setEntryUpdateVisible] = useState(false);
  const [importExportVisible, setImportExportVisible] = useState(false);
  const [entryAssignBulkVisible, setEntryAssignBulkVisible] = useState(false);
  const [entriesToAssign, setEntriesToAssign] = useState<string[]>([]);
  const [bulkEntryExportVisible, setBulkEntryExportVisible] = useState(false);
  //
  const [isBulkEntryAction, setIsBulkEntryAction] = useState(false);
  //
  const [selectedRows, setSelectedRows] = useState<React.Key[]>([]);
  const [duplicateEntries, setDuplicateEntries] = useState<Entry[]>([]);
  const [duplicateEntryVisible, setDuplicateEntryVisible] = useState(false);
  const [
    duplicateEntryInitiateResolveVisible,
    setDuplicateEntryInitiateResolveVisible,
  ] = useState(false);
  const [duplicateEntriesGroups, setDuplicateEntriesGroups] = useState<Record<
    string,
    Entry[]
  > | null>(null);
  //
  const [currentOnIgnoreAction, setCurrentOnIgnoreAction] = useState<
    (() => Promise<void>) | undefined
  >(undefined);
  //
  const [
    moduleDefinitionActivityDrawerVisible,
    setModuleDefinitionActivityDrawerVisible,
  ] = useState(false);
  const [moduleInstance, setModuleInstance] =
    useState<BaseModuleInstance | null>(null);

  const [moduleInstanceReadVisible, setModuleInstanceReadVisible] =
    useState(false);
  const [entryCreateAction, setEntryCreateAction] =
    useState<ATVoidFunction<CreateEntryDto> | null>(null);
  const [entryCreatePrefill, setEntryCreatePrefill] =
    useState<EntryCreatePrefill | null>(null);
  const [tagsManageVisible, setTagsManageVisible] = useState(false);
  //
  const [subToUnlock, setSubToUnlock] = useState<{
    visible: boolean;
    title?: string;
  }>({ visible: false, title: "" });
  //
  const [fahmyMenuOpen, setFahmyMenuOpen] = useState(false);
  const [fahmyCurrent, setFahmyCurrent] = useState<ChatWidgetCurrent>(
    ChatWidgetCurrent.DEFAULT,
  );
  const [fahmyShowTooltip, setFahmyShowTooltip] = useState(false);
  const [fahmyIsHovered, setFahmyIsHovered] = useState(false);

  const onSetSubToUnlock = (payload: typeof subToUnlock) => {
    setSubToUnlock({ ...payload });
  };

  return (
    <DrawersContext.Provider
      value={{
        task,
        setTask,
        taskKind,
        setTaskKind,
        tasksReadVisible,
        setTasksReadVisible,
        taskUpdateVisible,
        setTaskUpdateVisible,
        taskCreateVisible,
        setTaskCreateVisible,
        taskDeleteVisible,
        setTaskDeleteVisible,
        taskDoneVisible,
        setTaskDoneVisible,
        taskAssignVisible,
        setTaskAssignVisible,
        taskReadVisible,
        setTaskReadVisible,
        // Kanban
        kanbanCard,
        setKanbanCard,
        kanbanCardReadVisible,
        setKanbanCardReadVisible,
        kanbanCardUpdateVisible,
        setKanbanCardUpdateVisible,
        kanbanCardCreateVisible,
        setKanbanCardCreateVisible,
        kanbanSettingsVisible,
        setKanbanSettingsVisible,
        kanbanActivityVisible,
        setKanbanActivityVisible,
        kanbanCreateVisible,
        setKanbanCreateVisible,
        kanbanCardMovingReasonVisible,
        setKanbanCardMovingReasonVisible,
        kanbanCardMoveToOtherBoardVisible,
        setKanbanCardMoveToOtherBoardVisible,
        kanbanCardDeleteVisible,
        setKanbanCardDeleteVisible,
        kanbanAutomationVisible,
        setKanbanAutomationVisible,
        selectedKanbanColumnId,
        setSelectedKanbanColumnId,
        onDragKanbanCardWithReasonMessage,
        setOnDragKanbanCardWithReasonMessage,
        kanbanCardMovingReasonStageType,
        setKanbanCardMovingReasonStageType,
        selectedEntryForKanbanCard,
        setSelectedEntryForKanbanCard,
        exportModalVisible,
        setExportModalVisible,
        // Entries
        entry,
        setEntry,
        entryAssignVisible,
        setEntryAssignVisible,
        entryDetailsVisible,
        setEntryDetailsVisible,
        entryConvertVisible,
        setEntryConvertVisible,
        entryCreateVisible,
        setEntryCreateVisible,
        entryDeleteVisible,
        setEntryDeleteVisible,
        entryDeleteLoading,
        setEntryDeleteLoading,
        entryUpdateVisible,
        setEntryUpdateVisible,
        importExportVisible,
        setImportExportVisible,
        entryAssignBulkVisible,
        setEntryAssignBulkVisible,
        entriesToAssign,
        setEntriesToAssign,
        selectedRows,
        setSelectedRows,
        duplicateEntries,
        setDuplicateEntries,
        duplicateEntryVisible,
        setDuplicateEntryVisible,
        duplicateEntryInitiateResolveVisible,
        setDuplicateEntryInitiateResolveVisible,
        duplicateEntriesGroups,
        setDuplicateEntriesGroups,
        currentOnIgnoreAction,
        setCurrentOnIgnoreAction,
        bulkEntryExportVisible,
        setBulkEntryExportVisible,
        //
        isBulkEntryAction,
        setIsBulkEntryAction,
        //
        entryCreateAction,
        setEntryCreateAction,
        entryCreatePrefill,
        setEntryCreatePrefill,
        tagsManageVisible,
        setTagsManageVisible,
        // Modules
        moduleDefinitionActivityDrawerVisible,
        setModuleDefinitionActivityDrawerVisible,
        moduleInstance,
        setModuleInstance,
        moduleInstanceReadVisible,
        setModuleInstanceReadVisible,
        //
        subToUnlock,
        setSubToUnlock: onSetSubToUnlock,
        //
        fahmyMenuOpen,
        setFahmyMenuOpen,
        fahmyCurrent,
        setFahmyCurrent,
        fahmyShowTooltip,
        setFahmyShowTooltip,
        fahmyIsHovered,
        setFahmyIsHovered,
      }}
    >
      {children}
    </DrawersContext.Provider>
  );
};
